import { notify, TYPE, DURATION } from "@8x8/oxygen-toaster";

import Debug, { snap } from "@/react/debug";
import { filterMap } from "@/react/collections";
import { JobNotificationsManager } from "@/react/features/jobs";
import { ERRORS } from "@/react/components/table/useTableMutations";

import applyChanges from "./applyChanges";

const getOrCreateEntry = (rowsToUpdate, entityId) => {
  if (!rowsToUpdate.has(entityId)) {
    rowsToUpdate.set(entityId, { [ERRORS]: {} });
  }

  return rowsToUpdate.get(entityId);
};

/**
 * Adds validation errors from a TAP BE response to the rowsToPatch map.
 *
 * This function processes an error object from the backend and associates
 * validation errors with the corresponding row in the table. If the error
 * is not tied to a specific column, it uses a fallback key to store the error.
 *
 * @param {Map} rowsToPatch - A "patch" map where each key is a row ID, and the
 *  value is an object containing errors for that row, grouped under "_errors".
 * @param {Object} errorData - An error object from the backend containing
 *  details about the validation error.
 * @param {Object} options - Additional options for processing the error.
 * @param {string} options.fallbackKey - A key to use for errors not tied to a
 *  specific column.
 * @returns {Map} - The updated rowsToPatch map with the new error added.
 */
export const addError = (rowsToPatch, errorData, options) => {
  const { message } = errorData;
  const { fallbackKey } = options;

  if (!errorData?.entityId) {
    Debug.error(
      "[addError] Error DTO from TAP BE does not contain object/entity ID.",
      snap(errorData)
    );

    return rowsToPatch;
  }

  const entityId = errorData?.entityId;
  const entry = getOrCreateEntry(rowsToPatch, entityId);
  const rowLevelErrors = entry[ERRORS];

  // Handle null or non-existing column name: use the fallback key
  if (errorData?.columnName == null) {
    if (rowLevelErrors?.[fallbackKey]?.length) {
      rowLevelErrors[fallbackKey].push(message);
    } else {
      rowLevelErrors[fallbackKey] = [message];
    }

    return rowsToPatch;
  }

  // Add columnName if it does not exist
  const columnName = errorData?.columnName;
  if (!Object.keys(rowLevelErrors).includes(columnName)) {
    rowLevelErrors[columnName] = [];
  }

  rowLevelErrors[columnName].push(message);
  return rowsToPatch;
};

/**
 * Create a response handler for async operations, which return async jobs
 * instead of returning response data directly.
 *
 * These cause no live table updates if the job creation succeeds, but will
 * add rows into the table, in "staging/newly added" mode, with full validation
 * feedback if job creation fails.
 *
 * @param {Map} currentRows - The relevant set of current staging rows (recently added
 *  OR updated, before any backend sync has happened)
 * @param {function} setRows - Used to update the collection of staging rows
 * @param {function} setData - Used to update table data
 * @param {string} responseMappingKey - Key used to map responses to rows (e.g., name or custom field).
 * @param {string} idKey - Key used to uniquely identify rows in the table.
 * @param {string | null} customFallbackKey - Fallback key for errors not tied to specific fields.
 * @param options
 * @return {(function(*): void)|*}
 */
const createBulkMutationResponseHandler = ({
  currentRows,
  setRows,
  setData,
  responseMappingKey,
  idKey,
  customFallbackKey = null,
  options = {},
}) => {
  return (response) => {
    const operationResult = response?.operationResult;
    if (!operationResult) {
      throw new Error(
        "Invalid basic TAP response structure: missing 'operationResult'."
      );
    }

    if (operationResult.success) {
      const jobId =
        operationResult.success?.jobId ?? operationResult.success?.id;
      if (!jobId) {
        throw new Error(
          "Invalid success response: missing 'jobId' or 'id' field."
        );
      }

      // Once the request is successful, use currentRows to clear the rows
      // that were part of the batch request from the table. Once the job
      // was created, the table will need to be updated manually after the
      // async task is completed for the updates to be visible.
      const keysToRemove =
        Array.from(currentRows.values()).map((row) => row?.[idKey]) ?? [];
      const shouldRemove = (item) => !keysToRemove.includes(item[idKey]);
      setRows((rows) => filterMap(rows, shouldRemove));
      setData((items) => items.filter(shouldRemove));

      JobNotificationsManager.showJobNotification(jobId, options?.buildMessage);
    }

    if (operationResult.failure) {
      const errors = operationResult.failure?.errors ?? [];
      const globalMessage = operationResult.failure?.message;
      const fallbackKey = customFallbackKey ?? responseMappingKey ?? idKey;

      // Step 1: Build rowsToUpdate with errors
      let rowsToUpdate = new Map();
      for (const error of errors) {
        const objectId = error?.object;
        const message = error?.message;
        if (!objectId || !message) {
          Debug.warn(
            `[createBulkMutationResponseHandler] No objectId or message in error DTO - cannot attribute to table data in: ${snap(
              error
            )}`
          );
          continue;
        }

        rowsToUpdate = addError(
          rowsToUpdate,
          {
            entityId: objectId,
            columnName: error?.field, // An object field should map to a column key in the table
            message,
          },
          { fallbackKey } // The fallbackKey is passed for cases where no such equivalence exists
        );
      }

      // Step 2: Update table data with rowsToUpdate
      setData((currentData) => applyChanges(currentData, rowsToUpdate, idKey));

      // Step 3: Notify about the batch error
      notify({
        type: TYPE.error,
        content: `Batch Operation Result: ${globalMessage}. Check individual row validation in the table.`,
        duration: DURATION.INFINITE,
      });
    }
  };
};

export default createBulkMutationResponseHandler;
